import React, { useEffect, useState } from 'react';
import useLogStore from '@/store/logStore';
import Button from "@/components/atoms/Button";
import Input from "@/components/atoms/Input";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Select from "@/components/molecules/Select";
import LogCategory from "@/types/LogCategory";
import User from "@/types/User";
import useAccess from "@/hooks/useAccess";
import { DatePicker } from "@/components/atoms/DatePicker";
import TimePicker from '@/components/atoms/TimePicker';
import Duration from '@/components/atoms/Duration';
import axios from 'axios';
import { toast } from "react-toastify";
import { Pencil } from 'lucide-react';
import { CirclePlay } from 'lucide-react';
import { toUtcDate } from "@/lib/utils";

type LogTicket = {
  id: number;
  displayId: string;
  subject: string;
};

const getFormSchema = (
  logCategory: LogCategory | null,
  ticket: LogTicket,
  isManual: boolean
) => {
  const baseSchema = z.object({
    log_category_id: z.string().min(1, "Log Category is required"),
    sub_log_category_id: z
      .union([z.string(), z.number(), z.null()])
      .refine(
        (value) =>
          logCategory?.children?.length
            ? value !== null && value !== ""
            : true,
        {
          message: "Sub Log Category is required",
        }
      ),
    internal_sub_ticket_type_id: z
      .union([z.string(), z.number()])
      .refine(
        (value) =>
          ticket.displayId !== "Non-Chargeable"
            ? value !== null && value !== ""
            : true,
        {
          message: "Internal Ticket Type is required",
        }
      ),
    /*internal_sub_ticket_type_count: z
      .number()
      .refine(
        (value) =>
          ticket.displayId !== "Non-Chargeable"
            ? value !== null && value > 0
            : true,
        {
          message: "Amount must be greater than 0",
        }
      ),*/
    comment: z
      .string()
      .nullable()
      .refine(
        (value) =>
          logCategory?.comment_required
            ? value !== null && value.trim() !== ""
            : true,
        {
          message: "Comment is required",
        }
      ),
  });

  if (isManual) {
    return baseSchema.extend({
      user_id: z
        .string()
        .nonempty({ message: "User is required" }),
      started_at: z.preprocess(
        (arg) => {
          if (typeof arg === "string" || arg instanceof Date) {
            return new Date(arg);
          }
          return arg;
        },
        z.date({
          required_error: "Start Date is required",
          invalid_type_error: "Invalid date format",
        })
      ),
      time: z
        .string()
        .nonempty({ message: "Start Time is required" }),
      duration: z
        .string()
        .nonempty({ message: "Duration is required" }),
    });
  }

  return baseSchema;
};

type formData = z.infer<ReturnType<typeof getFormSchema>> & {
  user_id?: string | number;
  started_at?: Date;
  duration?: string;
  time?: string;
};
interface ActiveLogProps {
  ticket: LogTicket;
  logCategories: LogCategory[];
  internalSubTicketTypes: {
    id: number;
    name: string;
    internal_ticket_type_id: number;
  }[];
  users: User[];
}

export const useLogForm = (logCategory: LogCategory | null, ticket: LogTicket, isManual: boolean) =>
  useForm<formData>({
    resolver: zodResolver(getFormSchema(logCategory, ticket, isManual)),
    defaultValues: {
      log_category_id: "",
      sub_log_category_id: "",
      internal_sub_ticket_type_id: "",
      //internal_sub_ticket_type_count: 0,
      comment: "",
      user_id: "",
      started_at: new Date(),
      duration: "",
      time: "09:00",
    }
  });

const ActiveLog: React.FC<ActiveLogProps> = ({ ticket, logCategories, internalSubTicketTypes, users }) => {
  const {
    isActive,
    isPaused,
    startTime,
    duration,
    startLog,
    resumeLog,
    pauseLog,
    stopLog,
    log,
  } = useLogStore();
  const [selectedLogCategory, setSelectedLogCategory] = useState<LogCategory | null>(() => {
    if (log) {
      return logCategories.find((category) => category.id.toString() === log.log_category_id.toString()) || null;
    }
    return null;
  });
  const [manualEntryMode, setManualEntryMode] = useState(false);
  const form = useLogForm(selectedLogCategory, ticket, manualEntryMode);
  const [displayTime, setDisplayTime] = useState('00:00:00');
  const [errors, setErrors] = useState<string[]>([]);
  const { getAccess, currentUser } = useAccess();
  const [toastMessage, setToastMessage] = useState<{success: boolean, message: string} | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [logComment, setLogComment] = useState<string | null>(log?.comment || null);

  useEffect(() => {
    let interval: number | undefined;

    if (isActive) {
      interval = window.setInterval(() => {
        const now = Date.now();
        const elapsed = isPaused || !startTime ? 0 : Math.floor((now - startTime) / 1000);
        const totalSeconds = duration + elapsed;
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        setDisplayTime(`${hours}:${minutes}:${seconds}`);
      }, 1000);
    } else {
      setDisplayTime('00:00:00');
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, isPaused, duration, startTime]);

  useEffect(() => {
    if (toastMessage) {
      if (toastMessage.success) {
        toast.success(toastMessage.message);
      } else {
        toast.error(toastMessage.message);
      }
    }
  }, [toastMessage]);

  const handleStart = async () => {
    const formData = form.getValues();
   
    setErrors([]);
    const result = await startLog({
      ticketId: ticket.id,
      logCategoryId: formData.log_category_id,
      subLogCategoryId: formData.sub_log_category_id,
      internalTicketTypeId: internalSubTicketTypes.find((type) => type.id === Number(formData.internal_sub_ticket_type_id))?.internal_ticket_type_id,
      internalSubTicketTypeId: formData.internal_sub_ticket_type_id,
      //internalSubTicketTypeCount: formData.internal_sub_ticket_type_count,
      comment: formData.comment,
    });

    if (!result.success && result.errors) {
      setErrors(result.errors);
    }
  };

  const handlePauseResume = async () => {
    if (isPaused) {
      setErrors([]);
      const result = await resumeLog();

      if (!result.success && result.errors) {
        setErrors(result.errors);
      }
    } else {
      pauseLog();
    }
  };

  const handleStop = async () => {
    const formData = form.getValues();

    setErrors([]);
    const result = await stopLog({
      comment: formData.comment,
    });
    
    if (!result.success && result.errors) {
      setErrors(result.errors);
    } else {
      form.reset({
        log_category_id: "",
        sub_log_category_id: "",
        internal_sub_ticket_type_id: "",
        //internal_sub_ticket_type_count: 0,
        comment: "",
        user_id: currentUser ? currentUser.id.toString() : "",
        started_at: new Date(),
        duration: "",
        time: "09:00",
      });
      setSelectedLogCategory(null);
      setLogComment(null);
      setToastMessage({ success: true, message: 'Log saved successfully' });
    }
  };

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  const processLogData = (data: formData) => {
    let updatedData = { 
      user_id: data.user_id,
      log_category_id: data.log_category_id,
      sub_log_category_id: data.sub_log_category_id,
      internal_sub_ticket_type_id: data.internal_sub_ticket_type_id,
      //internal_sub_ticket_type_count: data.internal_sub_ticket_type_count,
      comment: data.comment,
      started_at: data.started_at,
      log_duration: null as string | null,
      internal_ticket_type_id: null as number | null,
      ticket_id: ticket.id,
     };
     
    if (data.started_at && data.time) {
      const [hours, minutes] = data.time.split(":").map(Number);
      const updatedStartedAt = new Date(data.started_at);
    
      updatedData.started_at = new Date(
        updatedStartedAt.getFullYear(),
        updatedStartedAt.getMonth(),
        updatedStartedAt.getDate(),
        hours, 
        minutes
      );
    }
  
    if (data.duration) {
      const [hours, minutes, seconds] = data.duration
        .split(":")
        .map(Number);
      updatedData.log_duration = (hours * 3600 + minutes * 60 + seconds).toString();
    }
  
    const subTicketType = internalSubTicketTypes.find(
      (item) =>
        item.id === Number(updatedData.internal_sub_ticket_type_id)
    );
  
    if (subTicketType) {
      updatedData.internal_ticket_type_id = subTicketType.internal_ticket_type_id;
    }
  
    return updatedData;
  };

  const handleManualSubmit = async () => {
    setSubmitting(true);
    const formData = form.getValues();
    const logData = processLogData(formData);
    
    try {
      const csrfToken = getCsrfToken();
      const response = await axios.post(`/logs`, {
        log: logData
      }, {
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });
 
      if (response.data.success) {
        setErrors([]);
        form.reset({
          log_category_id: "",
          sub_log_category_id: "",
          internal_sub_ticket_type_id: "",
          //internal_sub_ticket_type_count: 0,
          comment: "",
          user_id: currentUser ? currentUser.id.toString() : "",
          started_at: new Date(),
          duration: "",
          time: "09:00",
        });
        setSelectedLogCategory(null);
        setManualEntryMode(false);
        setToastMessage({ success: true, message: 'Log created successfully' });
      } else {
        setErrors(response.data.errors || ['Failed to create log']);
      }

      setSubmitting(false);
      
    } catch (error) {
      if ((error as any).response) {
        setErrors(
          (error as any).response.data.errors || ['You do not have permission to perform this action.']
        );
      } else {
        setErrors(['An unexpected error occurred. Please try again.']);
      }
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (log) {
      form.reset({
        log_category_id: log.log_category_id?.toString(),
        sub_log_category_id: log.sub_log_category_id?.toString(),
        internal_sub_ticket_type_id: log.internal_sub_ticket_type_id?.toString(),
        //internal_sub_ticket_type_count: log.internal_sub_ticket_type_count,
      });
    }
  }, [log, form]);

  useEffect(() => {
    if (currentUser) {
      form.setValue("user_id", currentUser.id.toString());
    }
  }, [currentUser]);

  useEffect(() => {
    if (internalSubTicketTypes.length === 1) {
      form.setValue("internal_sub_ticket_type_id", internalSubTicketTypes[0].id.toString());
    }
  }, [internalSubTicketTypes, form]);
  
  
  return (
    <div className="w-full p-4 rounded-lg shadow-lg">
      <div className="flex w-full flex-col sm:flex-row">
        {/* <div className="sm:mr-4 w-full sm:w-44 flex flex-col justify-center items-center border-b sm:border-b-0 sm:border-r border-gray-300 pb-4 sm:pb-0">
          <h2 className="text-lg font-semibold">Add Log</h2>
        </div> */}
        <div className="flex flex-col gap-4 w-full">
          {/* Display Errors (Local State) */}
          {errors.length > 0 && (
            <div className="bg-red-100 text-red-700 p-2 rounded">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(
                manualEntryMode ? handleManualSubmit : handleStart
              )}
              className="space-y-4 w-full"
            >
              <div className="flex items-center gap-1">
                <FormField
                  control={form.control}
                  name="log_category_id"
                  render={({ field }) => (
                    <>
                      <FormLabel className="w-44 text-sm text-gray-500"><span className="text-red-500"> *</span> Log Category</FormLabel>
                      <FormItem className="flex-grow">
                        <FormControl>
                          <Select
                            className="h-8 w-full"
                            options={logCategories.map((category) => ({
                              label: category.name,
                              value: category.id.toString(),
                            }))}
                            value={
                              isActive
                                ? log.log_category_id?.toString() || ''
                                : selectedLogCategory?.id.toString() || field.value || ''
                            }
                            onChange={(value) => {
                              field.onChange(value);
                              const category = logCategories.find((category) => category.id === Number(value));
                                if (category) {
                                  setSelectedLogCategory(category);
                                }
                            }}
                            disabled={isActive}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </>
                  )}
                />
              </div>

              {/* Sub Category */}
              {selectedLogCategory?.children && selectedLogCategory.children.length > 0 && (
                <div className="flex items-center gap-1">
                  <FormField
                    control={form.control}
                    name="sub_log_category_id"
                    render={({ field }) => (
                      <>
                        <FormLabel className="w-44 text-sm text-gray-500"><span className="text-red-500"> *</span> Sub Category</FormLabel>
                        <FormItem className="flex-grow">
                          <FormControl>
                            <Select
                              className="h-8 w-full"
                              options={selectedLogCategory.children.map((subCategory) => ({
                                label: subCategory.name,
                                value: subCategory.id.toString(),
                              }))}
                              value={
                                isActive
                                  ? log.sub_log_category_id?.toString() || ''
                                  : field.value?.toString() ?? ''
                              }
                              onChange={field.onChange}
                              disabled={isActive}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </>
                    )}
                  />
                </div>
              )}

              {ticket.displayId !== 'Non-Chargeable' && (
                <>
                  <div className="flex items-center gap-1">
                    <FormField
                      control={form.control}
                      name="internal_sub_ticket_type_id"
                      render={({ field }) => (
                        <>
                          <FormLabel className="w-44 text-sm text-gray-500"><span className="text-red-500"> *</span> Internal Sub Ticket Type</FormLabel>
                          <FormItem className="flex-grow">
                            <FormControl>
                              <Select
                                className="h-8 w-full"
                                options={internalSubTicketTypes.map((type) => ({
                                  label: type.name,
                                  value: type.id.toString(),
                                }))}
                                value={
                                  isActive
                                    ? log.internal_sub_ticket_type_id?.toString() || ''
                                    : field.value?.toString() ?? ''
                                }
                                onChange={field.onChange}
                                disabled={isActive}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </>
                      )}
                    />
                  </div>
                

        
                  {/*<div className="flex items-center gap-1">
                    <FormField
                      control={form.control}
                      name="internal_sub_ticket_type_count"
                      render={({ field }) => (
                        <>
                          <FormLabel className="w-44 text-sm text-gray-500"><span className="text-red-500"> *</span> Amount</FormLabel>
                          <FormItem className="flex-grow">
                            <FormControl>
                              <Input
                                type="number"
                                className="h-8 border border-gray-300 rounded-md px-2 w-full"
                                placeholder="0"
                                value={
                                  isActive
                                    ? log.internal_sub_ticket_type_count?.toString() || ''
                                    : field.value?.toString() ?? ''
                                }
                                onChange={(e) => {
                                  field.onChange(Number(e.target.value));
                                }}
                                disabled={isActive}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </>
                      )}
                    />
                  </div>*/}
                </>
                  
              )}
              {/* Comment */}
              <div className="flex items-center gap-1">
                <FormField
                  control={form.control}
                  name="comment"
                  render={({ field }) => (
                    <>
                      <FormLabel className="w-44 text-sm text-gray-500">Comment</FormLabel>
                      <FormItem className="flex-grow">
                        <FormControl>
                          <Input
                            type="text"
                            className="h-8 border border-gray-300 rounded-md px-2 w-full"
                            placeholder=""
                            value={logComment || field.value || ''}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              setLogComment(e.target.value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </>
                  )}
                />
              </div>

              {manualEntryMode && (
                <>
                  {getAccess(["create_log_for_others"]) ? (
                    <div className="flex items-center gap-1">
                      <FormField
                        control={form.control}
                        name="user_id"
                        render={({ field }) => (
                          <>
                            <FormLabel className="w-44 text-sm text-gray-500"><span className="text-red-500"> *</span> User</FormLabel>
                            <FormItem className="flex-grow">
                              <FormControl>
                                <Select
                                  className="h-8 w-full"
                                  options={users.map((user) => ({
                                    label: user.name,
                                    value: user.id.toString(),
                                  }))}
                                  value={field.value?.toString() || (currentUser ? currentUser.id.toString() : '')}
                                  onChange={field.onChange}
                                  enabledSearch={true}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          </>
                        )}
                      />
                    </div>
                  ): (
                    <input
                      type="hidden"
                      name="user_id"
                      value={currentUser ? currentUser.id.toString() : ''}
                      ref={(input) => {
                        if (input) {
                          form.setValue("user_id", currentUser ? currentUser.id.toString() : '');
                        }
                      }}
                    />
                  )}
                  
                  <div className="flex items-center gap-1">
                    <FormField
                      control={form.control}
                      name="started_at"
                      render={({ field }) => (
                        <>
                          <FormLabel className="w-44 text-sm text-gray-500"><span className="text-red-500"> *</span> Start Date</FormLabel>
                          <FormItem className="flex-grow">
                            <FormControl>
                              <DatePicker
                                placeholder="Start Date"
                                value={field.value ? field.value : new Date()}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </>
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <FormField
                      control={form.control}
                      name="time"
                      render={({ field }) => (
                        <>
                          <FormLabel className="w-44 text-sm text-gray-500">
                            <span className="text-red-500"> *</span> Start Time
                          </FormLabel>
                          <FormItem className="flex-grow">
                            <FormControl>
                              <TimePicker
                                onChange={(value: string) => field.onChange(value)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </>
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <FormField
                      control={form.control}
                      name="duration"
                      render={({ field }) => (
                        <>
                          <FormLabel className="w-44 text-sm text-gray-500"><span className="text-red-500"> *</span> Duration</FormLabel>
                          <FormItem className="flex-grow">
                            <FormControl>
                              <Duration
                                onChange={(value: string) => field.onChange(value)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </>
                      )}
                    />
                  </div>
                </>
              )}


              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                {!isActive ? (
                  <>
                    {manualEntryMode ? (
                      <>
                        {/* Manual Entry Modunda Görünen Submit Butonu */}
                        <Button
                          type="button"
                          className="bg-[#37677F] text-white px-4 py-2 rounded-md"
                          onClick={form.handleSubmit(handleManualSubmit)}
                          disabled={submitting}
                        >
                          {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                      </>
                    ) : (
                      <>
                        {/* Normal Mod */}
                        {getAccess(["create_manual_log"]) || getAccess(["create_log_for_others"]) ? (
                          <>
                            <Button
                              type="button"
                              className="bg-[#37677F] text-white px-4 py-2 rounded-md"
                              onClick={() => setManualEntryMode(true)}
                            >
                              <Pencil size="24" />
                            </Button>
                            <Button
                              className="bg-[#37677F] text-white px-4 py-2 rounded-md"
                              onClick={form.handleSubmit(handleStart)}
                            >
                              <CirclePlay size="24" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            className="bg-[#37677F] text-white px-4 py-2 rounded-md"
                            onClick={form.handleSubmit(handleStart)}
                          >
                            <CirclePlay size="24" />
                          </Button>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Log Aktifken Görünen Butonlar */}
                    <Button
                      className="bg-[#37677F] text-white px-4 py-2 rounded-md"
                      type="button"
                      onClick={handlePauseResume}
                    >
                      {isPaused ? "Resume" : "Pause"}
                    </Button>

                    <Button
                      className="bg-[#37677F] text-white px-4 py-2 rounded-md"
                      onClick={handleStop}
                    >
                      Stop
                    </Button>
                  </>
                )}
                </div>
                {!manualEntryMode && (
                  <div>
                    <span className="text-yellow-600 text-2xl font-semibold">{displayTime}</span>
                  </div>
                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ActiveLog;
